'use client';

import {
  type MotionValue,
  motion,
  useMotionValueEvent,
  useTransform,
} from 'motion/react';
import { useState } from 'react';

import { cn } from '@/lib/utils';

import { RACE_SCALE, type RaceSegment } from '../data';

/**
 * One race track: overhead (hatched) + AI-work (solid) segments laid out on
 * the shared before-track axis, a playhead bound to the sim clock, and a
 * "finished" overlay once the clock passes this lane's total. Playhead and
 * overlay both derive from the MotionValue, so scrubbing stays consistent.
 */
export function RaceLane({
  tag,
  note,
  tone,
  segments,
  totalSeconds,
  simTime,
}: {
  tag: string;
  note: string;
  tone: 'before' | 'after';
  segments: RaceSegment[];
  totalSeconds: number;
  simTime: MotionValue<number>;
}) {
  const [finished, setFinished] = useState(false);

  const playheadLeft = useTransform(
    simTime,
    (t) => `${(Math.min(t, totalSeconds) / RACE_SCALE) * 100}%`
  );

  useMotionValueEvent(simTime, 'change', (t) => {
    const isDone = t >= totalSeconds;
    setFinished((current) => (current === isDone ? current : isDone));
  });

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span
          className={cn(
            'border px-2 py-0.5 font-mono text-[10px] font-semibold tracking-wider uppercase',
            tone === 'before'
              ? 'border-muted-foreground text-muted-foreground'
              : 'border-foreground bg-foreground text-background'
          )}
        >
          {tag}
        </span>
        <span className="hidden text-xs text-muted-foreground sm:inline">
          {note}
        </span>
        <span
          className={cn(
            'ml-auto font-mono text-sm font-semibold tabular-nums',
            tone === 'before' ? 'text-muted-foreground' : 'text-foreground'
          )}
        >
          {totalSeconds}s
        </span>
      </div>

      <div className="relative h-11 overflow-hidden border border-edge bg-muted/40">
        {segments.map((segment) => (
          <div
            key={`${segment.step}-${segment.kind}`}
            title={`${segment.kind === 'work' ? 'AI generation' : 'our overhead'} · ${segment.seconds}s`}
            className={cn(
              'absolute inset-y-0 border-r border-background/80',
              // One hatch for overhead in both eras: the amount of hatch is
              // the story, not its colour.
              segment.kind === 'work'
                ? 'bg-muted-foreground/25'
                : 'bg-[repeating-linear-gradient(45deg,var(--color-muted-foreground),var(--color-muted-foreground)_5px,transparent_5px,transparent_10px)]'
            )}
            style={{
              left: `${segment.startPercent}%`,
              width: `${segment.widthPercent}%`,
            }}
          >
            {segment.kind === 'work' && segment.widthPercent > 6 && (
              <span className="pointer-events-none absolute top-1/2 left-1.5 hidden -translate-y-1/2 font-mono text-[9px] text-foreground/70 sm:inline">
                {segment.step}
              </span>
            )}
          </div>
        ))}

        {/* Playhead: style writes bypass React (60fps safe). */}
        <motion.div
          aria-hidden
          className="absolute -inset-y-0.5 z-10 w-0.5 bg-foreground shadow-[0_0_0_2px_var(--color-background)]"
          style={{ left: playheadLeft }}
        />

        <div
          className={cn(
            'absolute inset-0 z-20 flex items-center justify-center bg-background/75 font-mono text-xs font-semibold tracking-wider uppercase transition-opacity duration-300',
            tone === 'before' ? 'text-muted-foreground' : 'text-foreground',
            finished ? 'opacity-100' : 'pointer-events-none opacity-0'
          )}
        >
          finished · {totalSeconds}s
        </div>
      </div>
    </div>
  );
}
